<template>
    <div class="master-template-library">
        <Spin v-if="isLoading && inModal" fix></Spin>
        <template v-else>
            <div class="master-template-library__header" :class="{ 'no-templates': !hasMasterTemplates }">
                <SectionNav />

                <MasterTemplatesSortBy @input="setSort" />
                <MasterTemplateTextFilter />
                <MasterTemplateSelectViewControl />

                <MultiviewSelectAllControl
                    v-if="isSelectAllVisible"
                    class="master-template-library__select-all"
                    :show-empty="false"
                    instance-name="masterTemplates"
                />

                <MasterTemplateUploadButton
                    v-if="canManageMasterTemplates && !selected.length"
                    class="master-template-library__upload"
                />

                <Button
                    v-if="canManageMasterTemplates && selected.length"
                    type="primary"
                    class="master-template-library__upload"
                    @click="$emit('add')"
                >
                    Add to campaign
                </Button>
            </div>

            <MultiviewSelectAllNotice instance-name="masterTemplates" />

            <hox-empty-message
                v-if="masterTemplateLibraryFilters.length && !hasMasterTemplates"
                class="master-template-library__no-matches"
            >
                <template #title>No templates to show</template>
                <template #content>
                    <p>Please check your filter selection or upload a new template by clicking "Add template"</p>
                </template>
            </hox-empty-message>

            <MasterTemplateSelect
                v-if="hasMasterTemplates || !masterTemplateLibraryFilters.length"
                class="master-template-library__template-select"
                :items="masterTemplateIds"
                :campaign-template-ids="campaignMasterTemplateIds"
                :sort-by="sortBy"
                instance-name="masterTemplates"
                @update="onUpdate"
                @select="onSelect"
            />
        </template>
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import bus from "@/bus";
import clientMasterTemplatesQuery from "@/apollo/queries/v2/ClientMasterTemplates.gql";

import { MasterTemplateLibraryAction, MasterTemplateLibraryGetters } from "@/store/modules/masterTemplateLibrary";
import { UiAction } from "@/store/modules/ui";
import { TemplatesSortBy, type TemplateSortByType } from "@/enums/masterTemplate";

import MasterTemplateUploadButton from "@/components/Campaign/MasterTemplates/MasterTemplateUploadButton.vue";
import MasterTemplateSelect from "@/components/Campaign/MasterTemplateSelect.vue";
import MasterTemplatesSortBy from "@/components/Campaign/MasterTemplatesSortBy.vue";
import MasterTemplateTextFilter from "@/components/Campaign/MasterTemplates/MasterTemplateTextFilter.vue";
import MasterTemplateSelectViewControl from "@/components/Campaign/MasterTemplateSelectViewControl.vue";
import MultiviewSelectAllControl from "@/components/Campaign/MultiviewSelectAllControl.vue";
import MultiviewSelectAllNotice from "@/components/Campaign/MultiviewSelectAllNotice.vue";
import SectionNav from "@/components/SectionNav.vue";

export default defineComponent({
    name: "MasterTemplateLibrary",

    components: {
        MasterTemplateSelectViewControl,
        MasterTemplateTextFilter,
        MasterTemplatesSortBy,
        MultiviewSelectAllNotice,
        MultiviewSelectAllControl,
        MasterTemplateSelect,
        MasterTemplateUploadButton,
        SectionNav
    },

    props: {
        campaignMasterTemplateIds: {
            type: [Array, null] as PropType<string[] | null>,
            default() {
                return null;
            }
        },
        inModal: {
            type: Boolean,
            default: false
        }
    },

    emits: ["add", "select"],

    data() {
        return {
            isLoading: true,
            selected: [] as string[],
            sortBy: TemplatesSortBy.WIDTH_ASC as TemplateSortByType
        };
    },

    computed: {
        canListMasterTemplates() {
            return this.$auth.userCan(this.$auth.Actions.CanListMasterTemplates, { clientId: this.clientId }, false);
        },

        canManageMasterTemplates() {
            return this.$auth.userCan(this.$auth.Actions.CanManageMasterTemplates, { clientId: this.clientId }, false);
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        hasMasterTemplates() {
            return !!this.masterTemplateIds.length;
        },

        isPermissionGranted() {
            return this.whoAmI && this.canListMasterTemplates;
        },

        isSelectAllVisible() {
            return this.campaignMasterTemplateIds !== null;
        },

        masterTemplateIds() {
            return this.$store.getters[MasterTemplateLibraryGetters.filteredMasterTemplateIds] as string[];
        },

        masterTemplateLibraryFilters() {
            return this.$store.state.masterTemplateLibrary.filters;
        },

        showSidebar() {
            return this.hasMasterTemplates || !!this.masterTemplateLibraryFilters.length;
        },

        whoAmI() {
            return this.$store.state.auth.me;
        }
    },

    watch: {
        showSidebar: {
            immediate: true,
            handler(val) {
                if (!this.inModal) {
                    this.$store.dispatch(val ? UiAction.OpenSidebar : UiAction.CloseSidebar);
                }
            }
        }
    },

    methods: {
        onSelect(selected: string[]) {
            this.selected = selected;
            this.$emit("select", selected);
        },

        async onUpdate(resolve: () => void) {
            await this.$apollo.queries.masterTemplates?.refetch();
            resolve();
        },

        setSort(sortBy: TemplateSortByType) {
            this.sortBy = sortBy;
        }
    },

    apollo: {
        masterTemplates: {
            query: clientMasterTemplatesQuery,
            variables() {
                return {
                    clientId: this.clientId
                };
            },
            manual: true,
            skip() {
                return !this.isPermissionGranted;
            },
            result({ data }) {
                this.$store.dispatch(MasterTemplateLibraryAction.SetMasterTemplates, data.masterTemplatesByClient);
                this.$store.dispatch(MasterTemplateLibraryAction.SyncFilters);

                this.isLoading = false;
            },

            error(e) {
                bus.$emit("apolloErrorEvent", e as Error);
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.master-template-library {
    display: flex;
    flex-direction: column;
}

.master-template-library__filter-tags {
    flex: 0 0 auto;
    margin: 22px 0;
    padding: 0 20px;

    &.filterTags--empty {
        margin: 0;
        padding: 0;
    }
}

.master-template-library__no-matches {
    margin-top: $spacing;
}

.master-template-library__header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex: 0 0 auto;
    padding: 0 20px;
    border-bottom: 1px solid $grey3;
    height: 60px;
    margin: -30px 0 0;

    &.no-templates {
        .multiview-select-all {
            visibility: hidden;
        }
    }
}

.master-template-library__select-all {
    margin-right: $spacing-semi-small;
}

.master-template-library__template-select {
    flex: 1;
}

.templates-modal .master-template-library {
    &__header {
        margin-top: 0;
        border-bottom: 0;
    }
}
</style>
